import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiService, tokenManager } from '../services/api';

const AuthContext = createContext();

// Hook: Diğer bileşenlerden kolay erişim için
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isGuest, setIsGuest] = useState(false);
    const [loading, setLoading] = useState(true);

    // --- 1. YARDIMCI: BAŞARILI GİRİŞ İŞLEMLERİ ---
    const processLoginSuccess = (access_token, userData) => {
        tokenManager.setToken(access_token);
        setUser(userData);
        setIsGuest(false);
        localStorage.setItem('burofuchs_user', JSON.stringify(userData));
        localStorage.removeItem('burofuchs_guest');
    };

    // --- 2. KULLANICI BİLGİSİNİ YENİLEME ---
    const refreshUser = useCallback(async () => {
        const token = tokenManager.getToken();
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await apiService.auth.me();
            setUser(response.data);
            setIsGuest(false);
            localStorage.setItem('burofuchs_user', JSON.stringify(response.data));
        } catch (error) {
            console.error("Oturum yenileme hatası:", error);
            if (error.response && error.response.status === 401) {
                tokenManager.removeToken();
                localStorage.removeItem('burofuchs_user');
                setUser(null);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // --- 3. 🔥 PROFİL GÜNCELLEME (EKSİK OLAN KISIM) ---
    const updateProfile = async (updateData) => {
        try {
            const response = await apiService.auth.updateMe(updateData);
            const updatedUser = response.data;
            
            // State ve LocalStorage senkronizasyonu
            setUser(updatedUser);
            localStorage.setItem('burofuchs_user', JSON.stringify(updatedUser));
            
            return { success: true, user: updatedUser };
        } catch (error) {
            console.error('Profile update error:', error);
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Profil güncellenemedi.' 
            };
        }
    };

    // --- 4. BAŞLANGIÇ KONTROLÜ ---
    useEffect(() => {
        const initAuth = async () => {
            const token = tokenManager.getToken();
            const storedGuest = localStorage.getItem('burofuchs_guest');
            if (token) await refreshUser();
            else if (storedGuest === 'true') {
                setIsGuest(true);
                setLoading(false);
            } else setLoading(false);
        };
        initAuth();
    }, [refreshUser]);

    // --- 5. GİRİŞ YÖNTEMLERİ ---

    const login = async (email, password) => {
        try {
            const response = await apiService.auth.login({ email, password });
            const { access_token, user: userData } = response.data;
            processLoginSuccess(access_token, userData);
            return { success: true, user: userData };
        } catch (error) {
            return { success: false, error: error.response?.data?.detail || 'Giriş başarısız.' };
        }
    };

    const register = async (name, email, password) => {
        try {
            await apiService.auth.register({ name, email, password });
            return await login(email, password);
        } catch (error) {
            return { success: false, error: error.response?.data?.detail || 'Kayıt başarısız.' };
        }
    };

    const googleLogin = async (googleToken) => {
        try {
            const response = await apiService.auth.googleLogin(googleToken);
            const { access_token, user: userData } = response.data;
            processLoginSuccess(access_token, userData);
            return { success: true };
        } catch (error) {
            return { success: false, error: 'Google girişi başarısız.' };
        }
    };

    const appleLogin = async (response) => {
        try {
            const token = response.authorization.id_token;
            const userObj = response.user ? JSON.stringify(response.user) : null;
            const apiRes = await apiService.auth.appleLogin(token, userObj);
            const { access_token, user: userData } = apiRes.data;
            processLoginSuccess(access_token, userData);
            return { success: true };
        } catch (error) {
            return { success: false, error: 'Apple girişi başarısız.' };
        }
    };

    const loginAsGuest = () => {
        setIsGuest(true);
        setUser(null);
        localStorage.setItem('burofuchs_guest', 'true');
        tokenManager.removeToken();
        localStorage.removeItem('burofuchs_user');
    };

    const logout = () => {
        setUser(null);
        setIsGuest(false);
        tokenManager.removeToken();
        localStorage.removeItem('burofuchs_user');
        localStorage.removeItem('burofuchs_guest');
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            isGuest, 
            loading, 
            login, 
            register, 
            logout, 
            loginAsGuest, 
            googleLogin, 
            appleLogin, 
            refreshUser, 
            updateProfile, // ✅ ARTIK EKLENDİ VE ÇALIŞACAK
            isAuthenticated: !!user || isGuest 
        }}>
            {children}
        </AuthContext.Provider>
    );
};